import React, { useCallback, useEffect, useState } from "react";
import {
  styled,
  TableCell,
  Paper,
  Button,
  Grid,
  Box,
  TableContainer,
  TableHead,
  TableRow,
  TableBody,
  Table,
} from "@mui/material";
import { tableCellClasses } from "@mui/material/TableCell";
import InvoiceServices from "../../services/InvoiceService";
import { CreateDebitCreditNote } from "./CreateDebitCreditNote";
import { useNotificationHandling } from "../../Components/useNotificationHandling ";
import { MessageAlert } from "../../Components/MessageAlert";
import { CustomLoader } from "../../Components/CustomLoader";
import SearchComponent from "../../Components/SearchComponent ";
import { CustomPagination } from "../../Components/CustomPagination";
import { Popup } from "../../Components/Popup";
import { DebitCreditInvoiceNote } from "./DebitCreditInvoiceNote";
import CustomDateFilterPopup from "../../Components/CustomDateFilterPopup";
import { formatDate } from "../../utility/dateUtils";

export const DebitCreditView = () => {
  const [open, setOpen] = useState(false);
  const [debitCreditNotesData, setDebitCreditNotesData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [openCreateDebitCreditNotePopUp, setOpenCreateDebitCreditNotePopUp] =
    useState(false);
  const [openInvoiceNote, setOpenInvoiceNote] = useState(false);
  const [invoiceNoteData, setInvoiceNoteData] = useState();
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const [appliedStartDate, setAppliedStartDate] = useState(null);
  const [appliedEndDate, setAppliedEndDate] = useState(null);
  const [openDateFilter, setOpenDateFilter] = useState(false);
  const { handleError, handleCloseSnackbar, alertInfo } =
    useNotificationHandling();
  // const [startDate, setStartDate] = useState(null);
  // const [endDate, setEndDate] = useState(null);
  // const [openDateFilter, setOpenDateFilter] = useState(false);
  const getDebitCreditNotesData = useCallback(async () => {
    try {
      setOpen(true);

      const formattedStartDate = appliedStartDate
        ? appliedStartDate.toISOString().split("T")[0]
        : "";

      const formattedEndDate = appliedEndDate
        ? appliedEndDate.toISOString().split("T")[0]
        : "";

      const response = await InvoiceServices.getDebitCreditnotes(
        currentPage,
        searchQuery,
        formattedStartDate,
        formattedEndDate,
      );

      console.log("Debit/credit", response.data);

      setDebitCreditNotesData(
        response.data && response.data.results ? response.data.results : [],
      );

      setTotalPages(
        response.data && response.data.count
          ? Math.ceil(response.data.count / 25)
          : 0,
      );
    } catch (error) {
      handleError(error);
    } finally {
      setOpen(false);
    }
  }, [currentPage, searchQuery, appliedStartDate, appliedEndDate]); // Ensure dependencies are correctly listed

  useEffect(() => {
    getDebitCreditNotesData();
  }, [getDebitCreditNotesData]);

  const handleSearch = (query) => {
    setSearchQuery(query);
    setCurrentPage(1); // Reset to first page with new search
  };
  const handleDateFilterSubmit = () => {
    if (!startDate || !endDate) {
      handleError("Please select both start date and end date");
      return;
    }

    setAppliedStartDate(startDate);
    setAppliedEndDate(endDate);
    setCurrentPage(1);
    setOpenDateFilter(false);
  };
  const handleDateFilterReset = () => {
    setStartDate(null);
    setEndDate(null);
    setAppliedStartDate(null);
    setAppliedEndDate(null);
    setCurrentPage(1);
    setOpenDateFilter(false);
  };

  const handleReset = () => {
    setSearchQuery("");
    setCurrentPage(1); // Reset to first page with no search query
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const openPopUp = (data) => {
    setInvoiceNoteData(data);
    setOpenInvoiceNote(true);
  };
  const handleDownloadCSV = async () => {
    try {
      setOpen(true);

      const formattedStartDate = appliedStartDate
        ? appliedStartDate.toISOString().split("T")[0]
        : "";

      const formattedEndDate = appliedEndDate
        ? appliedEndDate.toISOString().split("T")[0]
        : "";

      const allData = [];
      let page = 1;
      let totalCount = 0;
      const pageSize = 25;

      do {
        const response = await InvoiceServices.getDebitCreditnotes(
          page,
          searchQuery,
          formattedStartDate,
          formattedEndDate,
        );

        const responseData = response.data || {};
        const results = responseData.results || [];

        if (page === 1) {
          totalCount = responseData.count || 0;
        }

        allData.push(...results);

        page += 1;

        if (results.length === 0) {
          break;
        }
      } while (allData.length < totalCount);

      if (allData.length === 0) {
        handleError("No data available to download");
        return;
      }

      const headers = [
        "Date",
        "Note Type",
        "Voucher No",
        "Customer",
        "Complaint No",
        "Reason",
        "GST %",
        "Amount",
        "Total Amount",
      ];

      const csvRows = allData.map((row) => [
        row.creation_date || "",
        row.note_type || "",
        row.note_no || "",
        row.customer || "",
        row.ccf_complain_no || "N/A",
        row.reason || "",
        row.gst_percentage + "%" || "",
        row.amount || "",
        row.total_amount || "",
      ]);

      const csvContent = [headers, ...csvRows]
        .map((row) =>
          row
            .map((value) => {
              const stringValue = String(value);
              return `"${stringValue.replace(/"/g, '""')}"`;
            })
            .join(","),
        )
        .join("\n");

      const blob = new Blob([csvContent], {
        type: "text/csv;charset=utf-8;",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.setAttribute(
        "download",
        `debit_credit_notes_${new Date().toISOString().split("T")[0]}.csv`,
      );

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);
    } catch (error) {
      handleError(error);
    } finally {
      setOpen(false);
    }
  };
  return (
    <>
      <MessageAlert
        open={alertInfo.open}
        onClose={handleCloseSnackbar}
        severity={alertInfo.severity}
        message={alertInfo.message}
      />
      <CustomLoader open={open} />

      <Grid item xs={12}>
        <Paper sx={{ p: 2, m: 4, display: "flex", flexDirection: "column" }}>
          <Box
            sx={{
              p: 2,
            }}
          >
            <Grid container spacing={2} alignItems="center">
              {/* Search Component on the left */}
              <Grid item xs={12} md={4}>
                <SearchComponent
                  onSearch={handleSearch}
                  onReset={handleReset}
                />
              </Grid>

              {/* Title Text centered */}
              <Grid
                item
                xs={12}
                md={4}
                sx={{ textAlign: { xs: "center", md: "center" } }}
              >
                <h3
                  style={{
                    margin: 0,
                    fontSize: "24px",
                    color: "rgb(34, 34, 34)",
                    fontWeight: 800,
                  }}
                >
                  Debit/Credit Notes
                </h3>
              </Grid>
              <Grid
                item
                xs={12}
                md={4}
                sx={{
                  display: "flex",
                  justifyContent: { xs: "center", md: "flex-end" },
                  gap: 1,
                }}
              >
                <Button
                  color="primary"
                  variant="outlined"
                  onClick={() => setOpenDateFilter(true)}
                >
                  Date Filter
                </Button>

                <Button
                  color="success"
                  variant="outlined"
                  onClick={handleDownloadCSV}
                >
                  Download CSV
                </Button>

                <Button
                  color="primary"
                  variant="contained"
                  onClick={() => setOpenCreateDebitCreditNotePopUp(true)}
                >
                  Add
                </Button>
              </Grid>
            </Grid>
          </Box>

          <TableContainer
            sx={{
              maxHeight: 440,
              "&::-webkit-scrollbar": {
                width: 15,
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "#f2f2f2",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#aaa9ac",
              },
            }}
          >
            <Table
              sx={{ minWidth: 1200 }}
              stickyHeader
              aria-label="sticky table"
            >
              <TableHead>
                <TableRow>
                  <StyledTableCell align="center">Date</StyledTableCell>
                  <StyledTableCell align="center">Note Type</StyledTableCell>
                  <StyledTableCell align="center">Voucher No</StyledTableCell>
                  <StyledTableCell align="center">Customer</StyledTableCell>
                  <StyledTableCell align="center">Complaint NO</StyledTableCell>
                  <StyledTableCell align="center">Reason</StyledTableCell>
                  <StyledTableCell align="center">GSt</StyledTableCell>
                  <StyledTableCell align="center">AMOUNT</StyledTableCell>

                  <StyledTableCell align="center">Total Amount</StyledTableCell>
                  <StyledTableCell align="center">ACTION</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {debitCreditNotesData.map((row, i) => (
                  <StyledTableRow key={i}>
                    <StyledTableCell align="center">
                      {row.creation_date}
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      {row.note_type}
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      {row.note_no}
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      {row.customer}
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      {row.ccf_complain_no || "N/A"}
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      {row.reason}
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      {row.gst_percentage}%
                    </StyledTableCell>

                    <StyledTableCell align="center">
                      {row.amount}
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      {row.total_amount}
                    </StyledTableCell>

                    <StyledTableCell align="center">
                      <Button
                        color="info"
                        variant="text"
                        onClick={() => openPopUp(row)}
                      >
                        View
                      </Button>
                    </StyledTableCell>
                  </StyledTableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <CustomPagination
            totalPages={totalPages}
            currentPage={currentPage}
            handlePageChange={handlePageChange}
          />
        </Paper>
        <Popup
          maxWidth="xl"
          title="Create Debit/Credit Notes"
          openPopup={openCreateDebitCreditNotePopUp}
          setOpenPopup={setOpenCreateDebitCreditNotePopUp}
        >
          <CreateDebitCreditNote
            getDebitCreditNotesData={getDebitCreditNotesData}
            setOpenPopup={setOpenCreateDebitCreditNotePopUp}
          />
        </Popup>

        <Popup
          fullScreen={true}
          title="Debit/Credit Note"
          openPopup={openInvoiceNote}
          setOpenPopup={setOpenInvoiceNote}
        >
          <DebitCreditInvoiceNote
            getDebitCreditNotesData={getDebitCreditNotesData}
            setOpenPopup={setOpenInvoiceNote}
            invoiceNoteData={invoiceNoteData}
          />
        </Popup>
        <Popup
          title="Date Filter"
          openPopup={openDateFilter}
          setOpenPopup={setOpenDateFilter}
        >
          <CustomDateFilterPopup
            open={openDateFilter}
            setOpen={setOpenDateFilter}
            startDate={startDate}
            endDate={endDate}
            setStartDate={setStartDate}
            setEndDate={setEndDate}
            onSubmit={handleDateFilterSubmit}
            onError={handleError}
          />
        </Popup>
      </Grid>
    </>
  );
};
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    fontSize: 12,
    backgroundColor: "#006BA1",
    color: theme.palette.common.white,
    fontWeight: "bold",
    textTransform: "uppercase",
    padding: 5,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 13,
    padding: 5,
  },
}));
const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));
